import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import isEmpty from "lodash/isEmpty";
import classnames from "clsx";
import get from "lodash/get";
import map from "lodash/map";
import groupBy from "lodash/groupBy";
import Headroom from "react-headroom";
import Page from "@/components/page";
import Loader from "@/components/loader";
import { commonLoaderStyle, LOADER_TYPES } from "@/constants";
import { useIsRTL } from "@/hooks";
import { useAppSelector } from "@/redux/store";
import {
  getBrandsData,
  resetBrandsReducer
} from "@/redux/actions/common.action";
import { withPageInitialLoad } from "@/libs/pageDataBridge";
import { selectBrandsData } from "./selectors";
import TitleHeader from "./titleHeader";
import CategoriesTabs from "./categoriesTabs";
import Brands from "./content";
import BrandsBreadcrumb from "./BrandsBreadcrumb";
import { isTouchScreen } from "@/util";
import { BrandData, SelectedCategory } from "./types";

const ShopByBrands = () => {
  const { t } = useTranslation("shopByBrands");
  const isRTL = useIsRTL();

  const brands = useAppSelector(selectBrandsData);
  const searchText = useAppSelector(state => state.brandsReducer.searchText);

  const [selectedCategory, setSelectedCategory] = useState<SelectedCategory>({
    name: "All",
    slug: "All"
  });
  const [selectedBrandChar, setSelectedBrandChar] = useState("A");

  const { sortCategories, filteredResult, categoryData, tabsData } =
    useMemo(() => {
      const categoryData = getCategoryData(brands, selectedCategory);

      const tabsData = map(brands, brand => ({
        name: brand.name,
        slug: brand.slug
      }));

      const filteredCategoryData =
        searchText && categoryData?.brands
          ? Object.keys(categoryData.brands).map(key =>
              categoryData.brands[key].filter(data =>
                data.brand.value
                  .toLowerCase()
                  .startsWith(searchText.toLowerCase())
              )
            )
          : [];

      const filteredResult =
        filteredCategoryData.find(filteredData => !isEmpty(filteredData)) || [];

      const sortCategories = getSortedCategories(categoryData);

      return { sortCategories, filteredResult, categoryData, tabsData };
    }, [brands, selectedCategory, searchText]);

  return (
    <Page
      title={t("ourBrands")}
      description={t("metaDescription")}
      id="shop-by-brands"
      indexable
      className="position-relative"
    >
      <BrandsBreadcrumb className="to_fixed" />
      {!isEmpty(brands) ? (
        <div
          className={classnames("shop_all_brands_wrapper", {
            arabic: isRTL
          })}
        >
          <div className="to_fixed">
            <TitleHeader count={categoryData?.count ?? 0} />
            <Headroom disableInlineStyles>
              <CategoriesTabs
                tabs={tabsData}
                selectedTab={selectedCategory}
                selectedBrandChar={selectedBrandChar}
                categoryData={sortCategories}
                setSelectedCategory={setSelectedCategory}
                brands={brands}
                setSelectedBrandChar={setSelectedBrandChar}
                searchText={searchText}
              />
            </Headroom>
          </div>
          <div className="brands-wrapper">
            <div
              className={classnames("container padding-web-0", {
                scrollWrapper: isTouchScreen() && brands.length
              })}
            >
              {searchText ? (
                <Brands
                  groupName={searchText.charAt(0).toUpperCase()}
                  data={filteredResult}
                  selectedCategory={selectedCategory}
                />
              ) : (
                get(categoryData, "brands") &&
                sortCategories.map(key => (
                  <Brands
                    key={key}
                    groupName={key}
                    data={categoryData.brands[key]}
                    selectedCategory={selectedCategory}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      ) : (
        <Loader
          type={LOADER_TYPES.DOTS}
          size={16}
          qty={3}
          visible
          style={commonLoaderStyle}
        />
      )}
    </Page>
  );
};

export const getSortedCategories = (categoryData: BrandData) => {
  const categories = Object.keys(get(categoryData, "brands", {}));

  const { str = [], num = [] } = groupBy(categories, category =>
    isNaN(parseInt(category)) ? "str" : "num"
  );

  return [...str, ...num];
};

export const getCategoryData = (
  brands: BrandData[],
  selectedCategory: SelectedCategory
) => brands.find(brand => brand.slug === selectedCategory.slug);

export default withPageInitialLoad({
  page: ShopByBrands,
  pageName: "brands",
  ssr: true,
  strategy: async ({ store }) => {
    store.dispatch(resetBrandsReducer());
    await store.dispatch(getBrandsData());
  },
  loader: false
});
